/* Efficiency lines across the days of a month — stages on the Trends tab,
 * shifts on the Shifts tab.
 *
 * Unlike the banded charts built on Plot, the points here run edge to edge:
 * the first day sits on the left axis and the last on the right, as the
 * design draws them. So hit-testing is to the nearest point rather than to
 * a band, and the readout is pinned to the top of the plot beside the guide
 * instead of following the cursor.
 *
 * The scale is fixed at 40–120% rather than fitted. Every chart on the page
 * shares it, which is what lets a stage line, a shift line and a machine's
 * bars be compared by eye; values outside it are clamped to the frame.
 */

import { useRef, useState } from 'react'
import type { ReactNode } from 'react'

export const EFF_LO = 40
export const EFF_HI = 120
export const EFF_TICKS = [40, 60, 80, 100, 120]

/** Efficiency -> y pixel on the shared 40–120 scale, clamped to the frame. */
export function effY(eff: number, height: number): number {
  return height - Math.max(0, Math.min(1, (eff - EFF_LO) / (EFF_HI - EFF_LO))) * height
}

export interface DaySeries {
  label: string
  color: string
  /** SVG dash pattern, for series that have to share a colour. */
  dash?: string
  /** One value per day; null where there is nothing to plot. */
  values: (number | null)[]
}

/** The y-axis labels, shared with the machine detail's bars. */
export function EffAxis({ height }: { height: number }) {
  return (
    <div className="num" style={{
      display: 'flex', flexDirection: 'column-reverse', justifyContent: 'space-between',
      alignItems: 'flex-end', height, margin: '-7px 0', fontSize: 11,
      color: 'var(--color-neutral-600)',
    }}>
      {EFF_TICKS.map((t) => <span key={t}>{t}</span>)}
    </div>
  )
}

/** A legend swatch drawn as the line it stands for, dashes included. */
export function LineSwatch({ color, dash }: { color: string; dash?: string }) {
  return (
    <svg width="16" height="3" style={{ display: 'block', flex: 'none' }}>
      <line x1="0" y1="1.5" x2="16" y2="1.5" stroke={color} strokeWidth="3"
        strokeDasharray={dash} />
    </svg>
  )
}

export function MachineDayLines({
  days, series, height, bench, benchLabel, strokeWidth = 1.8, tipWidth = 190, title,
}: {
  /** The x labels — day of month, one per point. */
  days: string[]
  series: DaySeries[]
  height: number
  bench: number
  benchLabel: string
  strokeWidth?: number
  tipWidth?: number
  /** The readout's heading for day `i`. */
  title: (i: number) => ReactNode
}) {
  const box = useRef<HTMLDivElement>(null)
  const [hov, setHov] = useState<number | null>(null)
  const n = days.length
  const xAt = (i: number) => (n > 1 ? i / (n - 1) : 0.5) * 1000

  const onMove = (e: React.MouseEvent) => {
    const el = box.current
    if (!el || n < 1) return
    const r = el.getBoundingClientRect()
    if (r.width <= 0) return
    const i = Math.round(((e.clientX - r.left) / r.width) * (n - 1))
    setHov(Math.max(0, Math.min(n - 1, i)))
  }

  const drawn = series.filter((s) => s.values.some((v) => v !== null))
  const frame = { display: 'grid', gridTemplateColumns: '34px minmax(0,1fr)', gap: 8 } as const

  return (
    <>
      <div style={{ ...frame, marginTop: 18 }}>
        <EffAxis height={height} />
        <div ref={box} style={{ position: 'relative' }}
          onMouseMove={onMove} onMouseLeave={() => setHov(null)}>
          <svg viewBox={`0 0 1000 ${height}`} preserveAspectRatio="none" style={{
            width: '100%', height, display: 'block',
            borderLeft: '1px solid var(--color-neutral-500)',
            borderBottom: '1px solid var(--color-neutral-500)',
          }}>
            {EFF_TICKS.map((t) => (
              <line key={t} x1="0" y1={effY(t, height)} x2="1000" y2={effY(t, height)}
                stroke="var(--c-grid)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            ))}
            <line x1="0" y1={effY(bench, height)} x2="1000" y2={effY(bench, height)}
              stroke="var(--c-ink)" strokeWidth="1.4" strokeDasharray="6 4"
              vectorEffect="non-scaling-stroke" />
            {drawn.map((s) => (
              <polyline key={s.label} fill="none" stroke={s.color}
                strokeWidth={strokeWidth} strokeDasharray={s.dash} strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                points={s.values
                  .map((v, i) => (v === null ? null
                    : `${xAt(i).toFixed(1)},${effY(v, height).toFixed(1)}`))
                  .filter(Boolean).join(' ')} />
            ))}
            {hov !== null ? (
              <line x1={xAt(hov)} y1="0" x2={xAt(hov)} y2={height}
                stroke="var(--c-ink)" strokeWidth="1" opacity="0.45"
                vectorEffect="non-scaling-stroke" />
            ) : null}
          </svg>
          {hov !== null ? (
            <div style={{
              position: 'absolute', top: 8, left: `${xAt(hov) / 10}%`,
              // Flipped to the left of the guide on the right-hand third,
              // so the card never hangs off the panel.
              transform: xAt(hov) > 650 ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
              pointerEvents: 'none', minWidth: tipWidth, padding: '10px 12px',
              background: 'var(--color-bg)', color: 'var(--color-text)',
              border: '1px solid var(--color-divider)', boxShadow: 'var(--shadow-md)',
              fontSize: 12, zIndex: 2,
            }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>{title(hov)}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {drawn.map((s) => (
                  <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <LineSwatch color={s.color} dash={s.dash} />
                    <span style={{ flex: 1, color: 'var(--color-neutral-700)' }}>{s.label}</span>
                    <span className="num" style={{ fontWeight: 600 }}>
                      {s.values[hov] === null ? '–' : `${(s.values[hov] as number).toFixed(1)}%`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div style={frame}>
        <div />
        <div style={{
          display: 'flex', justifyContent: 'space-between', fontSize: 10,
          color: 'var(--color-neutral-600)', marginTop: 5,
        }}>
          {days.map((d, i) => <span key={i}>{d}</span>)}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 14 }}>
        {drawn.map((s) => (
          <div key={s.label} style={{
            display: 'flex', alignItems: 'center', gap: 7, fontSize: 12,
            color: 'var(--color-neutral-800)',
          }}>
            <LineSwatch color={s.color} dash={s.dash} />{s.label}
          </div>
        ))}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 7, fontSize: 12,
          color: 'var(--color-neutral-600)',
        }}>
          <span style={{
            width: 16, height: 0, borderTop: '2px dashed var(--c-ink)', display: 'block',
          }} />Benchmark {benchLabel}
        </div>
      </div>
    </>
  )
}
